import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Stack, Button, Chip, Alert } from '@mui/material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const VoteAlignmentQuiz: React.FC = () => {
  const { authenticated, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statements, setStatements] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<any[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!authenticated) { setLoading(false); return; }
      try {
        setError(null);
        const resp = await apiService.getVoteAlignmentQuiz();
        if (cancelled) return;
        const items = Array.isArray(resp?.data?.statements) ? resp.data.statements : [];
        setStatements(items);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load quiz');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated]);

  const setAnswer = (id: number, val: number) => {
    setAnswers(prev => ({ ...prev, [id]: val }));
  };

  const onSubmit = async () => {
    if (!authenticated) { login(); return; }
    const ids = statements.map(s => s.id);
    const ans = ids.map((id: number) => (answers[id] ?? 0));
    setSubmitting(true);
    setError(null);
    try {
      const resp = await apiService.submitVoteAlignmentQuiz(ids, ans);
      const items = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      setResults(items);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Vote Alignment Quiz</Typography>
      {!authenticated && (
        <Alert severity="info" sx={{ mb: 2 }}>Sign in to take the quiz. <Button size="small" onClick={login}>Sign In</Button></Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Typography variant="body2" color="textSecondary">Loading…</Typography>
      ) : (
        <Card>
          <CardContent>
            {statements.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No statements available.</Typography>
            ) : (
              <Stack spacing={2}>
                {statements.map((s: any, idx: number) => (
                  <Box key={s.id}>
                    <Typography variant="subtitle1" gutterBottom>{idx + 1}. {(s.question || '').trim()}</Typography>
                    {/* Bill context (title + summary) if available */}
                    {s.bill && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} gutterBottom>
                          {s.bill.title || `${(s.bill.type || '').toUpperCase()} ${s.bill.number}`}
                        </Typography>
                        {s.bill.summary && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {s.bill.summary}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                          <Chip size="small" label={`Roll Call ${s.rollCall}`} />
                          <Button size="small" variant="outlined" onClick={() => s.bill?.route && navigate(s.bill.route)}>View bill</Button>
                        </Stack>
                      </Box>
                    )}
                    {!s.bill && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                        <Chip size="small" label={`Roll Call ${s.rollCall}`} />
                      </Stack>
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant={(answers[s.id] === 1) ? 'contained' : 'outlined'} size="small" onClick={() => setAnswer(s.id, 1)}>Support</Button>
                      <Button variant={(answers[s.id] === 0 || answers[s.id] === undefined) ? 'contained' : 'outlined'} size="small" onClick={() => setAnswer(s.id, 0)}>Neutral</Button>
                      <Button variant={(answers[s.id] === -1) ? 'contained' : 'outlined'} size="small" onClick={() => setAnswer(s.id, -1)}>Oppose</Button>
                    </Stack>
                  </Box>
                ))}
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={onSubmit} disabled={submitting}>See My Matches</Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {results && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Your Top Matches</Typography>
            {results.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No matches found.</Typography>
            ) : (
              <Stack spacing={1}>
                {results.map((m: any, idx: number) => (
                  <Alert key={idx} severity="success" sx={{ py: 0.5 }}>
                    {m.name} ({m.party || '—'}) — {m.state}{m.district ? `-${m.district}` : ''} • Score: {typeof m.score === 'number' ? m.score.toFixed(2) : m.score}
                  </Alert>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default VoteAlignmentQuiz;
